// Portfolio projects. Content sourced from the per-project .docx files the
// client provided; images live in public/portfolio/<slug>/NN.webp.

export type TLang = 'am' | 'en' | 'ru'

export const resolveLang = (lng?: string): TLang => {
  const l = (lng || 'am').slice(0, 2).toLowerCase()
  
return l === 'en' || l === 'ru' ? l : 'am'
}

export type TPortfolioCategory = 'residential' | 'public'

type TLocalized = Record<TLang, string>

export type TPortfolioProject = {
  slug: string
  category?: TPortfolioCategory
  year?: string
  imageCount: number
  videoCount?: number
  cover: string // file name within the project folder
  name: TLocalized
  title?: TLocalized
  address?: TLocalized
  description?: TLocalized
}

export type TMediaItem = { type: 'image' | 'video'; src: string; poster?: string }

export const portfolioImg = (slug: string, file: string) =>
  `${process.env.PUBLIC_URL}/portfolio/${slug}/${file}`

const pad = (n: number) => String(n).padStart(2, '0')

export const projectImages = (p: TPortfolioProject): string[] =>
  Array.from({ length: p.imageCount }, (_, i) => portfolioImg(p.slug, `${pad(i + 1)}.webp`))

// Compressed videos live alongside images as v01.mp4 … with a v01.webp poster.
export const projectVideos = (p: TPortfolioProject): Array<{ src: string; poster: string }> =>
  Array.from({ length: p.videoCount ?? 0 }, (_, i) => ({
    src: portfolioImg(p.slug, `v${pad(i + 1)}.mp4`),
    poster: portfolioImg(p.slug, `v${pad(i + 1)}.webp`),
  }))

// Unified gallery order: still images first, then videos.
export const projectMedia = (p: TPortfolioProject): TMediaItem[] => [
  ...projectImages(p).map(src => ({ type: 'image' as const, src })),
  ...projectVideos(p).map(v => ({ type: 'video' as const, src: v.src, poster: v.poster })),
]

const RESIDENTIAL_POWER: TLocalized = {
  am: 'Բազմաֆունկցիոնալ բնակելի համալիրի էլեկտրամատակարարման և կապի համակարգերի նախագծում',
  en: 'Design of power supply and communication systems for a multifunctional residential complex',
  ru: 'Проектирование систем электроснабжения и связи многофункционального жилого комплекса',
}

const KHANJYAN: TLocalized = {
  am: 'ք. Երևան, Խանջյան փողոց հ. 9/6',
  en: '9/6 Khanjyan Street, Yerevan',
  ru: 'г. Ереван, ул. Ханджяна, д. 9/6',
}

export const projects: TPortfolioProject[] = [
  {
    slug: 'hrazdan-gorge',
    category: 'public',
    year: '2025',
    imageCount: 2,
    cover: '01.webp',
    name: { am: 'ՀՐԱԶԴԱՆ ԿԻՐՃ', en: 'HRAZDAN GORGE', ru: 'РАЗДАНСКОЕ УЩЕЛЬЕ' },
    title: {
      am: 'Հրազդանի կիրճի ցածրահոս համակարգերի, լուսավորության, ջրահեռացման և ջրամատակարարման նախագծում',
      en: 'Design of low-current systems, lighting, drainage and water supply for the Hrazdan Gorge',
      ru: 'Проектирование слаботочных систем, освещения, водоотведения и водоснабжения Разданского ущелья',
    },
    address: {
      am: 'Հրազդանի կիրճ, ք. Երևան',
      en: 'Hrazdan Gorge, Yerevan',
      ru: 'Разданское ущелье, г. Ереван',
    },
  },
  {
    // Info to be filled in later — name only for now.
    slug: 'parket-avenue',
    imageCount: 1,
    videoCount: 2,
    cover: '01.webp',
    name: { am: 'Պարկետ Ավենյու', en: 'Parket Avenue', ru: 'Паркет Авеню' },
  },
  {
    // Info to be filled in later — name only for now.
    slug: 'ayntap',
    imageCount: 3,
    videoCount: 2,
    cover: '01.webp',
    name: { am: 'Այնթապ', en: 'Ayntap', ru: 'Айнтап' },
  },
  {
    // Info to be filled in later — name only for now.
    slug: 'monte-melkonyan',
    imageCount: 2,
    videoCount: 3,
    cover: '01.webp',
    name: { am: 'Մոնթե Մելքոնյան', en: 'Monte Melkonyan', ru: 'Монте Мелконян' },
  },
  {
    slug: 'ayb',
    category: 'public',
    year: '2026',
    imageCount: 2,
    cover: '02.webp',
    name: { am: 'ԱՅԲ', en: 'AYB', ru: 'Айб' },
    title: {
      am: 'ԱՅԲ դպրոցի նոր մասնաշենքի ինժեներական համակարգերի նախագծում',
      en: 'Engineering systems design for the new building of AYB School',
      ru: 'Проектирование инженерных систем нового корпуса школы AYB (Айб)',
    },
    address: {
      am: 'ք. Երևան, Թբիլիսյան խճ. հ. 11/11',
      en: '11/11 Tbilisyan Highway, Yerevan',
      ru: 'г. Ереван, Тбилисское шоссе, д. 11/11',
    },
  },
  {
    slug: 'ember',
    category: 'public',
    year: '2025',
    imageCount: 2,
    videoCount: 1,
    cover: '01.webp',
    name: { am: 'ԷՄԲԵՐ', en: 'EMBER', ru: 'ЭМБЕР' },
    title: {
      am: 'Բազմաֆունկցիոնալ հասարակական նշանակության շինության ինժեներական համակարգերի նախագծում',
      en: 'Engineering systems design for a multifunctional public building',
      ru: 'Проектирование инженерных систем многофункционального общественного здания',
    },
    address: {
      am: 'ք. Երևան, Մոնթե Մելքոնյան փողոց հ. 24',
      en: '24 Monte Melkonyan Street, Yerevan',
      ru: 'г. Ереван, ул. Монте Мелконяна, д. 24',
    },
  },
  {
    slug: 'luyser',
    category: 'public',
    year: '2025',
    imageCount: 2,
    cover: '01.webp',
    name: { am: 'ԼՈՒՅՍԵՐ', en: 'LUYSER', ru: 'ЛУЙСЕР' },
    title: {
      am: 'Բազմաֆունկցիոնալ հասարակական համալիրի ինժեներական համակարգերի նախագծում',
      en: 'Engineering systems design for a multifunctional public complex',
      ru: 'Проектирование инженерных систем многофункционального общественного комплекса',
    },
    address: {
      am: 'ք. Երևան, Լենինգրադյան փողոց հ. 29/12',
      en: '29/12 Leningradyan Street, Yerevan',
      ru: 'г. Ереван, ул. Ленинградян, д. 29/12',
    },
  },
  {
    slug: 'lot-12',
    category: 'residential',
    year: '2025',
    imageCount: 11,
    cover: '01.webp',
    name: { am: 'ԼՈՏ 12', en: 'LOT 12', ru: 'ЛОТ 12' },
    title: {
      am: 'Առևտրային տարածքներով բազմաֆունկցիոնալ բնակելի համալիրի ինժեներական համակարգերի նախագծում',
      en: 'Engineering systems design for a multifunctional residential complex with commercial spaces',
      ru: 'Проектирование инженерных систем многофункционального жилого комплекса с коммерческими помещениями',
    },
    address: {
      am: 'ք. Երևան, 33-րդ թաղամաս, թիվ 12 լոտ (կից և ներկառուցված տարածքներ)',
      en: 'Lot No. 12, 33rd District, Yerevan (attached and built-in premises)',
      ru: 'Лот №12, 33-й квартал, г. Ереван (пристроенные и встроенные помещения)',
    },
  },
  {
    slug: 'lot-14a',
    category: 'residential',
    year: '2025',
    imageCount: 6,
    cover: '01.webp',
    name: { am: 'ԼՈՏ 14Ա', en: 'LOT 14A', ru: 'ЛОТ 14А' },
    title: RESIDENTIAL_POWER,
    address: KHANJYAN,
  },
  {
    slug: 'lot-14b',
    category: 'residential',
    year: '2025',
    imageCount: 9,
    cover: '01.webp',
    name: { am: 'ԼՈՏ 14Բ', en: 'LOT 14B', ru: 'ЛОТ 14Б' },
    title: {
      am: 'Բազմաֆունկցիոնալ բնակելի շենքի ինժեներական նախագծում',
      en: 'Engineering design of a multifunctional residential building',
      ru: 'Проектирование инженерных систем многофункционального жилого здания',
    },
    address: KHANJYAN,
  },
  {
    slug: 'lot-10-11',
    category: 'residential',
    year: '2025',
    imageCount: 12,
    cover: '01.webp',
    name: { am: 'ԼՈՏ 10,11', en: 'LOT 10,11', ru: 'ЛОТ 10,11' },
    title: RESIDENTIAL_POWER,
    address: KHANJYAN,
  },
  {
    slug: 'davtashen-residence',
    category: 'residential',
    imageCount: 5,
    cover: '03.webp',
    name: {
      am: 'Դավթաշեն առանձնատուն',
      en: 'Davtashen Private Residence',
      ru: 'Частный дом в Давташене',
    },
    title: {
      am: 'Մասնավոր առանձնատան ճարտարապետական նախագիծ',
      en: 'Private residence — architectural concept',
      ru: 'Частный дом — архитектурная концепция',
    },
    address: {
      am: 'ք. Երևան, Դավթաշեն',
      en: 'Davtashen, Yerevan',
      ru: 'г. Ереван, Давташен',
    },
    description: {
      am: 'Այս ճարտարապետական հայեցակարգը ներկայացնում է ժամանակակից շքեղ բնակարան՝ մաքուր և ժամանակակից դիզայնով։ Նախագիծը համատեղում է մինիմալիստական ձևերը, մեծ ապակե ճակատները և ընդարձակ բացօթյա տարածքները՝ տան և դրա բնական միջավայրի միջև ամուր կապ ստեղծելու համար։',
      en: 'This architectural concept presents a modern luxury residence with a clean, contemporary design. The project combines minimalist forms, large glass façades and expansive outdoor spaces to create a strong connection between the home and its natural surroundings.',
      ru: 'Эта архитектурная концепция представляет современный роскошный дом с чистым, современным дизайном. Проект сочетает минималистичные формы, большие стеклянные фасады и обширные открытые пространства, создавая прочную связь между домом и окружающей природой.',
    },
  },
]

export const getProject = (slug?: string): TPortfolioProject | undefined =>
  projects.find(p => p.slug === slug)
